import React, { useState, useEffect, useRef, useCallback, useReducer } from 'react';
import { ChevronLeft, ChevronRight, X, Bookmark, List } from 'lucide-react';
import '../../styles/BookReader.css';
import SubNav from '../subNav';

// Reducer and initial state remain the same
const bookReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    case 'TOGGLE_BOOK':
      return { ...state, isBookOpen: action.payload };
    case 'SET_TURNING':
      return { ...state, isPageTurning: action.payload };
    case 'ADD_BOOKMARK':
      return { ...state, bookmarks: [...state.bookmarks, action.payload] };
    case 'REMOVE_BOOKMARK':
      return {
        ...state,
        bookmarks: state.bookmarks.filter(bookmark => bookmark !== action.payload)
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

const initialState = {
  currentPage: 0,
  isBookOpen: true,
  isPageTurning: false,
  bookmarks: [],
  isLoading: true,
  error: null
};

// Alert components remain the same
const AlertDialog = ({ children, isOpen, onClose }) => (
  isOpen ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="bg-black bg-opacity-50 absolute inset-0" onClick={onClose} />
      <div className="bg-white rounded-lg p-6 z-10 max-w-md w-full m-4">
        {children}
      </div>
    </div>
  ) : null
);

const AlertTitle = ({ children }) => (
  <h2 className="text-xl font-bold mb-4">
    {children}
  </h2>
);

const AlertDescription = ({ children }) => (
  <p className="text-gray-600 mb-6">
    {children}
  </p>
);

const AlertDialogAction = ({ children }) => (
  <div className="flex justify-end space-x-4">
    {children}
  </div>
);

// Table of Contents component remains the same
const TableOfContents = ({ pages, onNavigate, currentPage }) => (
  <div className="bg-white rounded-lg shadow-lg p-4 max-h-96 overflow-y-auto">
    <h3 className="text-lg font-bold mb-4">Table of Contents</h3>
    <ul className="space-y-2">
      {pages.map((page, index) => (
        <li
          key={index}
          className={`cursor-pointer hover:bg-gray-100 p-2 rounded ${currentPage === index ? 'bg-blue-100' : ''
            }`}
          onClick={() => onNavigate(index)}
        >
          {index === 0 ? 'Cover' :
            index === 1 ? 'Contents' :
              `Chapter ${index - 1}: ${page.substring(0, 30)}...`}
        </li>
      ))}
    </ul>
  </div>
);

const BookReader = () => {
  const [state, dispatch] = useReducer(bookReducer, initialState);
  const pageContainerRef = useRef(null);
  const pageRefs = useRef([]);
  const [showToc, setShowToc] = useState(false);
  const [bookPages, setBookPages] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pageCurlRef = useRef(null);
  const shadowRef = useRef(null);

  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0
  });

  const calculatePageCurl = useCallback((x, width) => {
    const progress = Math.max(0, Math.min(1, x / width));
    const angle = progress * 180;
    const shadow = Math.sin(progress * Math.PI) * 0.3;
    return { angle, shadow };
  }, []);

  // Moved animatePageTurn definition before it's used
  const animatePageTurn = useCallback((direction) => {
    const pageWidth = pageContainerRef.current?.offsetWidth || 0;
    const frames = 60;
    let frame = 0;

    const animate = () => {
      if (frame < frames) {
        const progress = frame / frames;
        const ease = 1 - Math.pow(1 - progress, 3);
        const { angle, shadow } = calculatePageCurl(ease * pageWidth, pageWidth);

        if (pageCurlRef.current) {
          pageCurlRef.current.style.transform = `rotateY(${direction === 'next' ? angle : -angle}deg)`;
          if (shadowRef.current) {
            shadowRef.current.style.opacity = shadow.toString();
          }
        }

        frame++;
        requestAnimationFrame(animate);
      } else {
        if (pageCurlRef.current) {
          pageCurlRef.current.style.transform = '';
          if (shadowRef.current) {
            shadowRef.current.style.opacity = '0';
          }
        }
      }
    };

    requestAnimationFrame(animate);
  }, [calculatePageCurl]);

  const changePage = useCallback((newPage) => {
    if (newPage >= 0 && newPage < bookPages.length && !state.isPageTurning) {
      dispatch({ type: 'SET_TURNING', payload: true });
      animatePageTurn(newPage > state.currentPage ? 'next' : 'prev');

      setTimeout(() => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
        dispatch({ type: 'SET_TURNING', payload: false });
      }, 500);
    }
  }, [state.isPageTurning, bookPages.length, animatePageTurn, state.currentPage]);

  const prevPage = useCallback(() => {
    changePage(state.currentPage - 1);
  }, [changePage, state.currentPage]);

  const nextPage = useCallback(() => {
    changePage(state.currentPage + 1);
  }, [changePage, state.currentPage]);

  const toggleBookmark = useCallback((page) => {
    if (state.bookmarks.includes(page)) {
      dispatch({ type: 'REMOVE_BOOKMARK', payload: page });
    } else {
      dispatch({ type: 'ADD_BOOKMARK', payload: page });
    }
  }, [state.bookmarks]);

  const handleMouseDown = useCallback((e) => {
    setDragState({
      isDragging: true,
      startX: e.clientX,
      currentX: e.clientX
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (dragState.isDragging) {
      setDragState(prev => ({
        ...prev,
        currentX: e.clientX
      }));

      const pageWidth = pageContainerRef.current?.offsetWidth || 0;
      const deltaX = e.clientX - dragState.startX;
      const { angle, shadow } = calculatePageCurl(Math.abs(deltaX), pageWidth);

      if (pageCurlRef.current) {
        pageCurlRef.current.style.transform = `rotateY(${deltaX > 0 ? -angle : angle}deg)`;
        if (shadowRef.current) {
          shadowRef.current.style.opacity = shadow.toString();
        }
      }
    }
  }, [dragState, calculatePageCurl]);

  const handleMouseUp = useCallback(() => {
    if (dragState.isDragging) {
      const deltaX = dragState.currentX - dragState.startX;
      const pageWidth = pageContainerRef.current?.offsetWidth || 0;

      if (Math.abs(deltaX) > pageWidth * 0.3) {
        if (deltaX > 0 && state.currentPage > 0) {
          prevPage();
        } else if (deltaX < 0 && state.currentPage < bookPages.length - 1) {
          nextPage();
        }
      }

      if (pageCurlRef.current) {
        pageCurlRef.current.style.transform = '';
        if (shadowRef.current) {
          shadowRef.current.style.opacity = '0';
        }
      }

      setDragState({
        isDragging: false,
        startX: 0,
        currentX: 0
      });
    }
  }, [dragState, state.currentPage, bookPages.length, nextPage, prevPage]);
  useEffect(() => {
    const bookPagesContent = [
      // Cover page
      "AI Script Writing Guide\nMaster the Art of AI-Assisted Creative Writing",

      // Table of Contents
      `Table of Contents

1. Generate Ideas
   - AI-Powered Story Generation
   - Theme Combinations
   - Creative Possibilities

2. Write Prompts
   - Prompt Crafting Techniques
   - Balancing Direction
   - Scene Generation

3. Edit AI-Generated Scripts
   - Advanced Editing Tools
   - Real-time Refinement
   - Style Adjustment

4. Download and View Scripts
   - Format Options
   - Sharing Capabilities
   - Viewing Experience

5. Analyze Script Metrics
   - Analytics Tools
   - Story Structure
   - Character Balance`,

      // Chapter 1
      `Generate Ideas

Unleash Your Creativity with Our AI-Powered Story Generator

Whether you're looking to craft a romantic drama, a thrilling mystery, or a sci-fi adventure, our AI-powered generator opens up endless creative possibilities. You can input themes, genres, or specific character traits and receive fresh, unique story ideas instantly.

The tool encourages experimentation, letting you combine different settings and genres to explore a wide range of creative suggestions that align with your vision.

Tip: Combine unexpected themes or settings, such as a fantasy set in a futuristic dystopia, to see how the AI responds. Often, the most unconventional ideas lead to the most captivating results.`,

      // Chapter 2
      `Write Prompts

Mastering the Art of Prompt Creation

Crafting the right prompt is key to getting the best output from the AI. Our tutorial helps you understand how to write prompts that guide the AI toward specific plot elements, character arcs, and scenes. Learn how to balance giving the AI direction while leaving room for creative AI interpretation.

Example: For a compelling AI-generated scene, try starting with a prompt like "A determined detective confronts a suspect in a rainy alleyway, but the truth is more complex than it seems."`,

      // Chapter 3
      `Edit AI-Generated Scripts

Advanced Editing and Refinement

Once you've generated a script, it's time to refine and polish it. Our editing suite offers advanced tools to help you tweak dialogue, enhance pacing, and adjust the tone to match your desired style. With real-time editing capabilities, you can quickly make improvements and see the results instantly.

Tip: Use the tone adjuster to switch between formal, conversational, or quirky styles to see how it changes the overall feel of a scene.`,

      // Chapter 4
      `Download and View Scripts

Script Management and Sharing

Download your scripts in various formats (PDF, Word, etc.) and view them in an optimized, scrollable format. Share your work easily with collaborators or keep a well-organized archive of your projects.

Whether you're on a mobile device or desktop, our responsive script viewer ensures a smooth reading experience.

Note: You can also export your scripts in industry-standard formats, ready for pitching or further development.`,

      // Chapter 5
      `Analyze Script Metrics

Data-Driven Story Analysis

Our analytics tools offer valuable insights into your script's structure. Analyze key metrics like:
• Word count
• Character dialogue distribution
• Scene breakdowns

This data helps you understand the pacing, balance character interactions, and make sure that the story flows naturally.

For instance, if you notice that one character dominates the dialogue, you can revise scenes to balance character interactions.`
    ];

    setBookPages(bookPagesContent);
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        prevPage();
      } else if (event.key === 'ArrowRight') {
        nextPage();
      } else if (event.key === 'Escape') {
        setShowConfirmDialog(true);
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [prevPage, nextPage]);

  useEffect(() => {
    const container = pageContainerRef.current;
    if (container) {
      container.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        container.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp]);

  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500">
        <p>Error loading book: {state.error}</p>
      </div>
    );
  }

  return (
    <>
    <SubNav/>
    <div className="book-reader" role="application" aria-label="Book Reader">
      {state.isBookOpen ? (
        <>
          <div className="flex justify-between items-center mb-4 p-4 bg-gray-100">
            <button
              onClick={() => setShowToc(!showToc)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              aria-label="Toggle table of contents"
            >
              <List size={20} />
              <span>Contents</span>
            </button>

            <div className="flex items-center gap-4">
              <span className="text-gray-600">
                Page {state.currentPage + 1} of {bookPages.length}
              </span>
              <button
                onClick={() => toggleBookmark(state.currentPage)}
                className={`p-2 rounded ${state.bookmarks.includes(state.currentPage)
                  ? 'text-yellow-500'
                  : 'text-gray-500'
                  }`}
                aria-label="Toggle bookmark"
              >
                <Bookmark size={20} />
              </button>
              <button
                onClick={() => setShowConfirmDialog(true)}
                className="p-2 rounded text-gray-500 hover:text-gray-700"
                aria-label="Close book"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {showToc && (
            <div className="absolute top-20 left-4 z-10">
              <TableOfContents
                pages={bookPages}
                onNavigate={(page) => {
                  dispatch({ type: 'SET_PAGE', payload: page });
                  setShowToc(false);
                }}
                currentPage={state.currentPage}
              />
            </div>
          )}

          <div
            className="page-container relative"
            ref={pageContainerRef}
            style={{
              display: 'flex',
              overflowX: 'hidden',
              perspective: '2000px',
              height: 'calc(100vh - 200px)',
              touchAction: 'none'
            }}
          >
            {bookPages.map((page, index) => (
              <div
                key={index}
                className={`page absolute inset-0 ${index === state.currentPage ? 'z-10' : 'z-0'
                  }`}
                ref={index === state.currentPage ? pageCurlRef : null}
                style={{
                  transform: `translateX(${(index - state.currentPage) * 100}%)`,
                  transformOrigin: 'left center',
                  transition: dragState.isDragging ? 'none' : 'transform 0.5s ease-in-out',
                  backgroundColor: '#f0f0f0',
                  padding: '2rem',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden'
                }}
              >
                <div className="max-w-2xl mx-auto relative">
                  <div
                    ref={index === state.currentPage ? shadowRef : null}
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to right, rgba(0,0,0,0.2), transparent)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out'
                    }}
                  />

                  <div className="text-2xl font-serif whitespace-pre-line">
                    {page}
                  </div>

                  {state.bookmarks.includes(index) && (
                    <div className="absolute top-4 right-4">
                      <Bookmark size={24} className="text-yellow-500" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="controls fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4 bg-white p-4 rounded-lg shadow-lg">
            <button
              onClick={prevPage}
              disabled={state.currentPage === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={20} />
              <span>Previous</span>
            </button>

            <button
              onClick={nextPage}
              disabled={state.currentPage === bookPages.length - 1}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <span>Next</span>
              <ChevronRight size={20} />
            </button>
          </div>
        </>
      ) : null}

      <AlertDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <AlertTitle>Close Book</AlertTitle>
        <AlertDescription>
          Are you sure you want to close the book? Your bookmarks will be lost.
        </AlertDescription>
        <AlertDialogAction>
          <button
            onClick={() => setShowConfirmDialog(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              dispatch({ type: 'TOGGLE_BOOK', payload: false });
              setShowConfirmDialog(false);
            }}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Close
          </button>
        </AlertDialogAction>
      </AlertDialog>

      <style jsx>{`
      .page {
        transform-style: preserve-3d;
        will-change: transform;
      }

      .page::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(to right, rgba(0,0,0,0.05), transparent 20%);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
      }

      .page.turning::after {
        opacity: 1;
      }

      @keyframes pageCurlShadow {
        0% { opacity: 0; }
        50% { opacity: 0.3; }
        100% { opacity: 0; }
      }

      .book-reader {
        background: #f8f9fa;
        min-height: 100vh;
      }

      .page-container::before,
      .page-container::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 50px;
        pointer-events: none;
        z-index: 2;
        background: linear-gradient(to right, rgba(0,0,0,0.05), transparent);
      }

      .page-container::before {
        left: 0;
        transform: scaleX(-1);
      }

      .page-container::after {
        right: 0;
      }
    `}</style>
    </div>
    </>
  );
};

export default BookReader;
